import { createStyles, Dialog, IconButton, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import clsx from 'clsx';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useModalStack } from 'src/components/account/organization/dashboard/modal/ModalStackContext';
import { usePhoneLayout } from 'src/components/account/organization/dashboard/primitives/usePhoneLayout';

type IconTone = 'red' | 'amber' | 'emerald' | 'rose' | 'blue' | 'orange';
type ModalWidth = 'default' | 'wide';

export interface ModalShellProps {
    id: string;
    open: boolean;
    onClose: () => void;
    eyebrow: string;
    title: string;
    icon?: React.ReactNode;
    iconTone?: IconTone;
    maxWidth?: ModalWidth;
    /** When true, backdrop click and Escape do not close the dialog; only the X / explicit onClose do. */
    disableQuickDismiss?: boolean;
    /** Fixed between the header and the scrolling body, e.g. a tab row that must not scroll away. */
    subHeader?: React.ReactNode;
    children: React.ReactNode;
}

const TONE_BG: Record<IconTone, string> = {
    red: '#fde2e0',
    amber: '#fffbeb',
    emerald: '#ecfdf5',
    rose: '#fef2f2',
    blue: '#eff6ff',
    orange: '#fff7ed',
};

const TONE_COLOR: Record<IconTone, string> = {
    red: '#93000b',
    amber: '#b45309',
    emerald: '#047857',
    rose: '#b91c1c',
    blue: '#1d4ed8',
    orange: '#c2410c',
};

const MAX_WIDTHS: Record<ModalWidth, number> = {
    default: 680,
    wide: 960,
};

/** The body's side padding — full-bleed children offset it with a negative margin of the same size. */
export const MODAL_GUTTER = 'var(--modal-shell-gutter, 24px)';

const useStyles = makeStyles(() =>
    createStyles({
        paper: {
            borderRadius: 16,
            boxShadow: '0 24px 64px rgba(28,28,25,0.18)',
            width: '100%',
            maxHeight: '88vh',
            display: 'flex',
            flexDirection: 'column',
        },
        paperFullScreen: {
            borderRadius: 0,
            maxHeight: 'none',
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            borderBottom: '1px solid #f3f4f6',
            flexShrink: 0,
        },
        headerCompact: {
            padding: '14px 16px',
        },
        headerLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: 12,
        },
        iconSquare: {
            width: 40,
            height: 40,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        },
        iconSquareRed: {
            backgroundColor: TONE_BG.red,
            color: TONE_COLOR.red,
        },
        iconSquareAmber: {
            backgroundColor: TONE_BG.amber,
            color: TONE_COLOR.amber,
        },
        iconSquareEmerald: {
            backgroundColor: TONE_BG.emerald,
            color: TONE_COLOR.emerald,
        },
        iconSquareRose: {
            backgroundColor: TONE_BG.rose,
            color: TONE_COLOR.rose,
        },
        iconSquareBlue: {
            backgroundColor: TONE_BG.blue,
            color: TONE_COLOR.blue,
        },
        iconSquareOrange: {
            backgroundColor: TONE_BG.orange,
            color: TONE_COLOR.orange,
        },
        iconInner: {
            fontSize: 22,
            display: 'flex',
        },
        textBlock: {
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
        },
        eyebrow: {
            color: '#6b7280',
            fontSize: 10,
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            lineHeight: 1,
        },
        title: {
            color: '#1c1c19',
            fontSize: 18,
            fontWeight: 800,
            lineHeight: 1.2,
        },
        closeButton: {
            width: 36,
            height: 36,
            '&:hover': {
                backgroundColor: '#f3f4f6',
            },
        },
        subHeader: {
            flexShrink: 0,
        },
        body: {
            padding: `20px ${MODAL_GUTTER}`,
            overflowY: 'auto',
            flex: 1,
        },
        bodyCompact: {
            paddingTop: 16,
            paddingBottom: 16,
        },
    })
);

const ModalShell: React.FC<ModalShellProps> = ({
    id,
    open,
    onClose,
    eyebrow,
    title,
    icon,
    iconTone = 'red',
    maxWidth = 'default',
    disableQuickDismiss = false,
    subHeader,
    children,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { openModal, closeModal, zIndexFor } = useModalStack();
    const fullScreen = usePhoneLayout();

    useEffect(() => {
        if (!open) {
            return;
        }
        openModal(id);
        return () => {
            closeModal(id);
        };
    }, [open, id, openModal, closeModal]);

    const zIndex = useMemo(() => zIndexFor(id), [zIndexFor, id]);

    const toneClass = clsx({
        [classes.iconSquareRed]: iconTone === 'red',
        [classes.iconSquareAmber]: iconTone === 'amber',
        [classes.iconSquareEmerald]: iconTone === 'emerald',
        [classes.iconSquareRose]: iconTone === 'rose',
        [classes.iconSquareBlue]: iconTone === 'blue',
        [classes.iconSquareOrange]: iconTone === 'orange',
    });

    return (
        <Dialog
            open={open}
            onClose={(_event, reason) => {
                if (disableQuickDismiss && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
                    return;
                }
                onClose();
            }}
            fullWidth
            fullScreen={fullScreen}
            maxWidth={false}
            style={{ zIndex }}
            PaperProps={{
                className: clsx(classes.paper, fullScreen && classes.paperFullScreen),
                style: fullScreen ? undefined : { maxWidth: MAX_WIDTHS[maxWidth] },
            }}
            BackdropProps={{
                style: { backgroundColor: 'rgba(28,28,25,0.45)', backdropFilter: 'blur(3px)' },
            }}>
            <div className={clsx(classes.header, fullScreen && classes.headerCompact)}>
                <div className={classes.headerLeft}>
                    {icon !== undefined && (
                        <div className={clsx(classes.iconSquare, toneClass)}>
                            <span className={classes.iconInner}>{icon}</span>
                        </div>
                    )}
                    <div className={classes.textBlock}>
                        <span className={classes.eyebrow}>{eyebrow}</span>
                        <span className={classes.title}>{title}</span>
                    </div>
                </div>
                <IconButton className={classes.closeButton} size="small" aria-label={t('close')} onClick={onClose}>
                    <CloseIcon style={{ fontSize: 18 }} />
                </IconButton>
            </div>
            {subHeader !== undefined && (
                <div className={classes.subHeader} data-testid="modal-shell-sub-header">
                    {subHeader}
                </div>
            )}
            <div
                className={clsx(classes.body, fullScreen && classes.bodyCompact)}
                style={{ '--modal-shell-gutter': fullScreen ? '16px' : '24px' } as React.CSSProperties}>
                {children}
            </div>
        </Dialog>
    );
};

export default ModalShell;
